import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle } from '@untitled-ui/icons-react';
import { useEffect, useState } from 'react';

import { Button } from '../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../../../components/dialog';
import { FeaturedIcon } from '../../../components/featured-icon';
import { setCustomMetaTitle } from '../../../components/meta-title';
import { MobileBlocker } from '../../../components/mobile-blocker';
import { ProductTemplatePage } from '../../../components/product-template-editor/product-template-page';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { usedByProducts } from '../../../sdk/product-template';
import {
  prefetchProductTemplateById,
  useProductTemplateById,
} from '../../../services/storage/product-template-queries';
import { tGlobal, tNamespace } from '../../../services/translation';
import { Feature, isFeatureAvailable } from '../../../shared/feature';

const t = tNamespace('app.product-template.edit');

export const Route = createFileRoute('/app/product-template/$id/edit')({
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
  beforeLoad: async function (args) {
    const { params } = args;
    await prefetchProductTemplateById(params.id);
  },
});

function RouteComponent() {
  const { id: productTemplateId } = Route.useParams();
  const [showEditModal, setShowEditModal] = useState(false);
  const [templateIsUsedByProducts, setTemplateIsUsedByProducts] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const template = useProductTemplateById(productTemplateId);
  const navigate = useNavigate();

  useEffect(
    function () {
      if (template?.data == null || !initialLoading) {
        return;
      }
      setInitialLoading(false);
      usedByProducts(productTemplateId)
        .then(function (res) {
          setTemplateIsUsedByProducts(res.data);
          setShowEditModal(res.data);
        })
        .catch(function (err) {
          setShowEditModal(false);
          captureException(
            err,
            {
              file: 'src/routes/app/product-template/$id.edit.tsx',
              method: 'useEffect',
              infoMessage: 'unable to fetch is template used by products',
            },
            trackError
          );
        });
    },
    [productTemplateId, initialLoading, template]
  );

  setCustomMetaTitle(template.data?.name ?? '', false);

  useEffect(
    function () {
      if (!isFeatureAvailable(Feature.PRODUCT)) {
        navigate({ to: '/app/blogs' }).catch(function (err) {
          captureException(
            err,
            {
              file: 'src/routes/app/product-template/$id.edit.tsx',
              method: 'navigate',
              infoMessage: 'unable to navigate to /app/blogs',
            },
            trackError
          );
        });
      }
    },
    [navigate]
  );
  if (template?.data == null) {
    return null;
  }

  return (
    <>
      <MobileBlocker />
      <ProductTemplatePage
        onClose={function () {
          navigate({ to: '/app/products/templates' }).catch(function (err) {
            captureException(
              err,
              {
                file: 'src/routes/app/product-template/$id.edit.tsx',
                method: 'onClose',
                infoMessage: 'unable to navigate to /app/products/templates',
              },
              trackError
            );
          });
        }}
        template={template.data}
        templateIsUsedByProducts={templateIsUsedByProducts}
      />
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <FeaturedIcon icon={<AlertTriangle />} color="yellow" size="md" className="m-auto mb-6" />
          <DialogTitle align="center">{t('warningModalTitle')}</DialogTitle>
          <DialogDescription align="center" className="mt-1">
            <span dangerouslySetInnerHTML={{ __html: t('warningModalDescription') }} />
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="outline"
              className="w-full"
              size="xl"
              onClick={function () {
                navigate({ to: `/app/products/templates` }).catch(function (err) {
                  captureException(
                    err,
                    {
                      file: 'src/routes/app/product-template/$id.edit.tsx',
                      method: 'onClick',
                      infoMessage: 'unable to navigate to /app/products/templates',
                    },
                    trackError
                  );
                });
              }}
            >
              {tGlobal('cancel')}
            </Button>
            <Button
              variant="accent"
              className="w-full"
              size="xl"
              onClick={() => setShowEditModal(false)}
            >
              {t('editTemplate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
